/**
 * Persists a guest's in-progress booking + tour selection across page
 * refreshes so the `/checkout/login` step stays usable when the user
 * reloads (Redux is in-memory and the cart endpoint 401s for guests).
 *
 * Only the subset of fields needed to rebuild the order recap and the
 * post-login `addToCart` call is serialised — we do NOT persist auth
 * state, prices in foreign currencies (recomputed live), or the cart
 * items array (we don't have one yet at this point in the funnel).
 *
 * Entries expire after 24h to avoid resurrecting a wildly stale tour
 * snapshot if the user opens the page weeks later.
 */

const STORAGE_KEY = "guydeez_guest_checkout";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface GuestCheckoutSnapshot {
  booking: {
    selectedDuration: any | null;
    travellers: {
      adults: number;
      children: number;
      infants: number;
    };
    datetime: { date: string | null; time: string | null };
    guideLanguage: string | null;
    meetingPoint: string | null;
    price: number;
  };
  tour: any;
  savedAt: number;
}

export const saveGuestCheckout = (
  snapshot: Omit<GuestCheckoutSnapshot, "savedAt">
): void => {
  if (typeof window === "undefined") return;
  try {
    const payload: GuestCheckoutSnapshot = {
      ...snapshot,
      savedAt: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / disabled storage — non-fatal */
  }
};

export const readGuestCheckout = (): GuestCheckoutSnapshot | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as GuestCheckoutSnapshot;
    if (!data?.tour?.uuid) return null;
    if (
      typeof data.savedAt !== "number" ||
      Date.now() - data.savedAt > MAX_AGE_MS
    ) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

export const clearGuestCheckout = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
};
